import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faClipboardList,
  faDolly,
  faExclamationTriangle,
  faEye,
  faSearch,
  faBoxOpen,
} from '@fortawesome/free-solid-svg-icons'
import './LogisticsDashboard.css'

const KPI_CARDS = [
  {
    key: 'open_requests',
    label: 'Requisições em aberto',
    icon: faClipboardList,
    tone: 'primary',
  },
  {
    key: 'late_requests',
    label: 'Requisições atrasadas',
    icon: faExclamationTriangle,
    tone: 'danger',
  },
  {
    key: 'pending_movements',
    label: 'Movimentos pendentes',
    icon: faDolly,
    tone: 'warning',
  },
]

const SITUATION_FALLBACK_LABELS = {
  late: 'Atrasada',
  partial: 'Parcial',
  pending: 'Pendente',
}

const formatDate = (value) => {
  if (!value) return '-'

  const normalizedValue =
    typeof value === 'string' ? value.replace(' ', 'T') : value
  const date = new Date(normalizedValue)

  if (Number.isNaN(date.getTime())) return '-'

  return date.toLocaleDateString('pt-BR')
}

const getItemsLabel = (request) => {
  const total = Number(request.total_items) || 0
  const sent = Number(request.sent_items) || 0

  if (total <= 0) return '-'

  return `${sent}/${total}`
}

const getItemsProgress = (request) => {
  const total = Number(request.total_items) || 0
  const sent = Number(request.sent_items) || 0

  if (total <= 0) return 0

  return Math.min(100, Math.round((sent / total) * 100))
}

const LogisticsKpiCard = ({ meta, value }) => (
  <article className={`logistics-kpi logistics-kpi--${meta.tone}`}>
    <div className="logistics-kpi-header">
      <span className="logistics-kpi-label">{meta.label}</span>
      <span className="logistics-kpi-icon" aria-hidden="true">
        <FontAwesomeIcon icon={meta.icon} />
      </span>
    </div>
    <strong className="logistics-kpi-value">{value ?? 0}</strong>
  </article>
)

const LogisticsDashboard = ({
  cards = {},
  requests = [],
  search = '',
  onSearchChange,
  onClearSearch,
  isFetching = false,
  onOpenRequest,
  onViewAll,
}) => {
  const openRequestsCount = cards.open_requests ?? 0
  const hasSearch = Boolean(String(search || '').trim())
  const panelCountLabel = hasSearch
    ? `${requests.length} de ${openRequestsCount}`
    : openRequestsCount

  return (
    <section className="logistics-dashboard">
      <div className="logistics-kpis">
        {KPI_CARDS.map((meta) => (
          <LogisticsKpiCard
            key={meta.key}
            meta={meta}
            value={cards[meta.key]}
          />
        ))}
      </div>

      <section
        className="logistics-panel"
        aria-labelledby="logistics-open-requests-title">
        <header className="logistics-panel-header">
          <div className="logistics-panel-heading">
            <div className="logistics-panel-title-row">
              <h2 id="logistics-open-requests-title">Requisições em aberto</h2>
              <span className="logistics-count-pill">{panelCountLabel}</span>
              {isFetching && (
                <span className="logistics-updating">
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  />
                  Atualizando
                </span>
              )}
            </div>
            <p className="logistics-panel-subtitle">
              Acompanhe as requisições que ainda precisam ser atendidas.
            </p>
          </div>

          <div className="logistics-tools">
            <div className="logistics-search">
              <FontAwesomeIcon
                icon={faSearch}
                className="logistics-search-icon"
                aria-hidden="true"
              />
              <input
                type="search"
                value={search}
                onChange={(event) => onSearchChange(event.target.value)}
                placeholder="Cliente ou nº da requisição"
                aria-label="Buscar por cliente ou número da requisição"
              />
              {hasSearch && (
                <button
                  type="button"
                  className="logistics-search-clear"
                  onClick={onClearSearch}
                  aria-label="Limpar busca">
                  ×
                </button>
              )}
            </div>

            {onViewAll && (
              <button
                type="button"
                className="logistics-view-all"
                onClick={onViewAll}>
                Ver todas
              </button>
            )}
          </div>
        </header>

        <div className="logistics-table-wrapper">
          <table className="logistics-table">
            <thead>
              <tr>
                <th>Requisição</th>
                <th>Cliente</th>
                <th>Pedido / Previsão</th>
                <th>Itens enviados</th>
                <th>Situação</th>
                <th className="logistics-table-action-heading">Ação</th>
              </tr>
            </thead>
            <tbody>
              {requests.length === 0 ? (
                <tr>
                  <td colSpan={6} className="logistics-empty">
                    Nenhuma requisição encontrada
                    {hasSearch ? ` para “${search}”` : ''}.
                  </td>
                </tr>
              ) : (
                requests.map((request) => {
                  const situation = request.situation || 'pending'
                  const situationLabel =
                    request.situation_label ||
                    SITUATION_FALLBACK_LABELS[situation] ||
                    'Pendente'
                  const isLate = request.is_late || situation === 'late'
                  const itemsLabel = getItemsLabel(request)
                  const itemsProgress = getItemsProgress(request)

                  return (
                    <tr
                      key={request.id}
                      className={isLate ? 'is-late' : undefined}>
                      <td className="logistics-request-cell">
                        <span className="logistics-request-number">
                          #{request.id}
                        </span>
                      </td>
                      <td>
                        <div className="logistics-customer">
                          <span className="logistics-customer-name">
                            {request.customer || '-'}
                          </span>
                          {request.organization && (
                            <span className="logistics-organization">
                              {request.organization}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="logistics-dates">
                          <span>{formatDate(request.created_at)}</span>
                          <small>
                            {request.deadline
                              ? `prev. ${formatDate(request.deadline)}`
                              : 'sem previsão'}
                          </small>
                        </div>
                      </td>
                      <td>
                        <div className="logistics-items">
                          <span className="logistics-items-value">
                            {itemsLabel}
                          </span>
                          {itemsLabel !== '-' && (
                            <span
                              className="logistics-items-bar"
                              aria-hidden="true">
                              <span
                                style={{ width: `${itemsProgress}%` }}
                              />
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span
                          className={`logistics-badge logistics-badge--${situation}`}>
                          {situationLabel}
                        </span>
                        {request.has_awaiting_purchase_items && (
                          <span className="logistics-badge logistics-badge--awaiting" title="Possui itens aguardando compra">
                            <FontAwesomeIcon icon={faBoxOpen} aria-hidden="true" />{' '}
                            Aguard. compra
                          </span>
                        )}
                      </td>
                      <td className="logistics-table-action-cell">
                        <button
                          type="button"
                          className="logistics-action"
                          onClick={() => onOpenRequest(request)}
                          title={`Abrir requisição #${request.id}`}
                          aria-label={`Abrir requisição #${request.id}`}>
                          <FontAwesomeIcon icon={faEye} aria-hidden="true" />
                          Abrir
                        </button>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        <footer className="logistics-footer">
          <span>
            {requests.length}{' '}
            {requests.length === 1
              ? 'requisição exibida'
              : 'requisições exibidas'}
          </span>
          <span className="logistics-footer-hint">
            Busque por cliente ou pelo número da requisição.
          </span>
        </footer>
      </section>
    </section>
  )
}

export default LogisticsDashboard
